/**
 * ConfidenceBadge Component
 *
 * TurboTax-style confidence indicator for auto-filled fields.
 * Shows confidence level (HIGH, MEDIUM, LOW) with visual cues.
 *
 * Usage:
 * <ConfidenceBadge level="high" source="Address geocoding" />
 */

import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  HelpCircle,
  AlertTriangle,
  Sparkles,
  Info,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'verified';

interface ConfidenceBadgeProps {
  level: ConfidenceLevel;
  source?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const confidenceConfig: Record<
  ConfidenceLevel,
  {
    label: string;
    shortLabel: string;
    icon: React.ElementType;
    color: string;
    bgColor: string;
    borderStyle: string;
    description: string;
  }
> = {
  verified: {
    label: 'VERIFIED',
    shortLabel: 'VERIFIED',
    icon: CheckCircle2,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderStyle: 'border-green-300 dark:border-green-700',
    description: 'Confirmed from official source',
  },
  high: {
    label: 'AUTO-FILLED',
    shortLabel: 'HIGH',
    icon: Sparkles,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderStyle: 'border-blue-300 dark:border-blue-700 border-solid',
    description: 'Auto-filled with high confidence',
  },
  medium: {
    label: 'INFERRED',
    shortLabel: 'MED',
    icon: HelpCircle,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    borderStyle: 'border-amber-300 dark:border-amber-700 border-dashed',
    description: 'Inferred from context - please verify',
  },
  low: {
    label: 'VERIFY',
    shortLabel: 'LOW',
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    borderStyle: 'border-red-300 dark:border-red-700 border-dotted',
    description: 'Low confidence - verification required',
  },
};

export function ConfidenceBadge({
  level,
  source,
  showLabel = true,
  size = 'sm',
  className,
}: ConfidenceBadgeProps) {
  const config = confidenceConfig[level];
  const Icon = config.icon;

  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border font-medium transition-all',
        config.bgColor,
        config.color,
        config.borderStyle,
        size === 'sm' ? 'px-1.5 py-0.5 text-micro' : 'px-2 py-1 text-xs',
        level === 'low' && 'animate-pulse',
        className
      )}
    >
      <Icon className={cn(size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
      {showLabel && <span>{config.label}</span>}
    </span>
  );

  if (source) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-medium">{config.description}</p>
              <p className="text-xs text-muted-foreground">Source: {source}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
}

/**
 * AutoFilledField - Wrapper for fields that were auto-filled
 *
 * Usage:
 * <AutoFilledField
 *   label="Jurisdiction"
 *   value="City of Jacksonville"
 *   confidence="high"
 *   source="Address geocoding"
 *   onOverride={() => setShowOverride(true)}
 * />
 */

interface AutoFilledFieldProps {
  label: string;
  value: string | React.ReactNode;
  confidence: ConfidenceLevel;
  source?: string;
  onOverride?: () => void;
  overrideLabel?: string;
  className?: string;
}

export function AutoFilledField({
  label,
  value,
  confidence,
  source,
  onOverride,
  overrideLabel = 'Override',
  className,
}: AutoFilledFieldProps) {
  const config = confidenceConfig[confidence];

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <ConfidenceBadge level={confidence} source={source} />
      </div>
      <div
        className={cn(
          'rounded-lg border p-3 transition-all',
          config.bgColor,
          config.borderStyle
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{value}</span>
          {onOverride && confidence !== 'verified' && (
            <button
              onClick={onOverride}
              className={cn(
                'text-xs underline-offset-2 hover:underline',
                config.color
              )}
            >
              {overrideLabel}
            </button>
          )}
        </div>
        {source && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Info className="h-3 w-3" />
            {confidence === 'verified' ? 'Verified via' : 'Auto-detected from'}: {source}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * ConfirmationRequired - Shows when low confidence fields need explicit confirmation
 */

interface ConfirmationItem {
  field: string;
  value: string;
  source?: string;
  risk?: string;
}

interface ConfirmationRequiredProps {
  items: ConfirmationItem[];
  onConfirm: (confirmedItems: string[]) => void;
  onCancel: () => void;
  className?: string;
}

export function ConfirmationRequired({
  items,
  onConfirm,
  onCancel,
  className,
}: ConfirmationRequiredProps) {
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());

  const toggleConfirm = (field: string) => {
    const newConfirmed = new Set(confirmed);
    if (newConfirmed.has(field)) {
      newConfirmed.delete(field);
    } else {
      newConfirmed.add(field);
    }
    setConfirmed(newConfirmed);
  };

  const allConfirmed = items.every((item) => confirmed.has(item.field));

  return (
    <div
      className={cn(
        'rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4',
        className
      )}
    >
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-amber-800 dark:text-amber-200">
            Confirm Before Submission
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
            The following items have LOW CONFIDENCE and may cause rejection:
          </p>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        {items.map((item) => (
          <div
            key={item.field}
            className="bg-white dark:bg-gray-900 rounded-md p-3 border border-amber-200 dark:border-amber-800"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed.has(item.field)}
                onChange={() => toggleConfirm(item.field)}
                className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{item.field}</span>
                  <span className="text-sm text-muted-foreground">{item.value}</span>
                </div>
                {item.source && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Source: {item.source}
                  </p>
                )}
                {item.risk && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Risk: {item.risk}
                  </p>
                )}
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                  {confirmed.has(item.field)
                    ? '✓ I confirm this value is correct'
                    : '□ I confirm this value is correct'}
                </p>
              </div>
            </label>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(Array.from(confirmed))}
          disabled={!allConfirmed}
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
            allConfirmed
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
          )}
        >
          Confirm & Submit Anyway
        </button>
      </div>
    </div>
  );
}

import { useState } from 'react';

export default ConfidenceBadge;
